import React, { Fragment, useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import { cloneDeep } from 'lodash'
import { Popover } from 'antd'
import { withNamespaces } from '@edulastic/localization'
import {
  Tabs,
  Tab,
  MathSpan,
  highlightSelectedText,
  RefContext,
  FlexContainer,
  getRangeAtFirst,
} from '@edulastic/common'

import {
  getFormattedAttrId,
  constrainRangeToContainer,
} from '@edulastic/common/src/helpers'
import { sanitizeHtml } from '@edulastic/common/src/utils/html'
import { tokenHighlightEditorTools } from '@edulastic/common/src/components/FroalaPlugins/constants'

import {
  WORD_MODE,
  PARAGRAPH_MODE,
  SENTENCE_MODE,
  CUSTOM_MODE,
} from '../../constants/constantsForQuestions'
import { updateVariables } from '../../utils/variables'
import QuestionTextArea from '../../components/QuestionTextArea'
import { Subtitle } from '../../styled/Subtitle'
import Question from '../../components/Question'
import { Container } from './styled/Container'
import { ModeButton } from './styled/ModeButton'
import { TemplateWrapper } from './styled/TemplateWrapper'

import {
  getInitialArray,
  getParagraphsArray,
  getSentencesArray,
  getWordsArray,
  getCustomArray,
  getCustomTokenTemplate,
} from './helpers'
import { extractAndProcessTokensFromContainer } from './utils'

const Template = ({
  item,
  setQuestionData,
  template,
  setTemplate,
  templateTab,
  setTemplateTab,
  t,
  fillSections,
  cleanSections,
}) => {
  const mode = item.tokenization
  const containerRef = useRef()
  const [customTokenCount, updateCount] = useState(0)

  const customHighlightHelpText = (
    <>
      <p>{t('component.tokenHighlight.customHelpText')}</p>
    </>
  )

  const handleItemChange = (prop, propData) => {
    setQuestionData(
      produce(item, (draft) => {
        if (prop === 'template') {
          let resultArray = ''
          const initialArray = getInitialArray(propData)
          if (mode === WORD_MODE) {
            resultArray = getWordsArray(initialArray)
          } else if (mode === PARAGRAPH_MODE) {
            resultArray = getParagraphsArray(initialArray)
          } else if (mode === CUSTOM_MODE) {
            resultArray = getCustomArray(initialArray)
          } else {
            resultArray = getSentencesArray(initialArray)
          }
          setTemplate(resultArray)
        }

        draft[prop] = propData
        updateVariables(draft)
      })
    )
  }

  const handleTemplateClick = (i) => () => {
    const newTemplate = cloneDeep(template)
    if (mode !== CUSTOM_MODE) {
      setQuestionData(
        produce(item, (draft) => {
          newTemplate[i].active = !newTemplate[i].active

          draft.templeWithTokens = newTemplate

          setTemplate(newTemplate)
          updateVariables(draft)
        })
      )
    }
  }
  // if element is custom token, this will return true.
  const isCustomToken = (elem) =>
    elem.className === 'token active-word' && !!elem.getAttribute('sequence')

  const removeCustomToken = (elem) => {
    if (!isCustomToken(elem)) {
      return
    }
    const tokenIndex = parseInt(elem.getAttribute('sequence'), 10)
    const newItem = produce(item, (draft) => {
      if (
        draft.templeWithTokens?.[tokenIndex] &&
        draft.templeWithTokens?.[tokenIndex]?.active
      ) {
        draft.templeWithTokens[tokenIndex].active = false
      }
    })
    setQuestionData(newItem)
  }

  const handleCustomTokenize = () => {
    const range = getRangeAtFirst()
    let constrainedRange = range
    if (range && containerRef.current) {
      constrainedRange = constrainRangeToContainer(range, containerRef.current)
    }
    if (
      constrainedRange &&
      highlightSelectedText(
        'edu-highlight-token',
        undefined,
        undefined,
        undefined,
        constrainedRange
      )
    ) {
      updateCount(customTokenCount + 1)
    }
  }

  const handleCustomTokenClick = (e) => removeCustomToken(e.target)

  const customTokenTemplate = getCustomTokenTemplate(item.templeWithTokens)

  useEffect(() => {
    if (containerRef.current) {
      const tokensWithCompletedTags = extractAndProcessTokensFromContainer(
        containerRef.current
      )
      setQuestionData(
        produce(item, (draft) => {
          draft.templeWithTokens = tokensWithCompletedTags
        })
      )
    }
  }, [customTokenCount])

  return (
    <TemplateWrapper>
      <Question
        section="main"
        label={t('component.tokenHighlight.templateTitle')}
        fillSections={fillSections}
        cleanSections={cleanSections}
      >
        <Subtitle
          id={getFormattedAttrId(
            `${item?.title}-${t('component.tokenHighlight.templateTitle')}`
          )}
        >
          {t('component.tokenHighlight.templateTitle')}
        </Subtitle>
        <Tabs
          style={{ marginBottom: 15 }}
          value={templateTab}
          onChange={setTemplateTab}
        >
          <Tab label={t('component.tokenHighlight.editTemplateTab')} />
          <Tab label={t('component.tokenHighlight.editTokenTab')} />
        </Tabs>

        {templateTab === 0 && (
          <QuestionTextArea
            onChange={(val) => handleItemChange('template', val)}
            value={item.template}
            toolbarId="tokens-template"
            border="border"
            buttons={tokenHighlightEditorTools}
            sanitizeClipboardHtml
            disableHTMLPaste
            allowQuickInsert={false}
          />
        )}

        {templateTab === 1 ? (
          <>
            <Container justifyContent="flex-start">
              <ModeButton
                active={mode === PARAGRAPH_MODE}
                onClick={() => handleItemChange('tokenization', PARAGRAPH_MODE)}
              >
                {t('component.tokenHighlight.paragraph')}
              </ModeButton>
              <ModeButton
                active={mode === SENTENCE_MODE}
                onClick={() => handleItemChange('tokenization', SENTENCE_MODE)}
              >
                {t('component.tokenHighlight.sentence')}
              </ModeButton>
              <ModeButton
                active={mode === WORD_MODE}
                onClick={() => handleItemChange('tokenization', WORD_MODE)}
              >
                {t('component.tokenHighlight.word')}
              </ModeButton>
              <FlexContainer alignItems="center" marginLeft="0" mr="0">
                <Popover
                  content={customHighlightHelpText}
                  trigger={['hover', 'focus']}
                >
                  <ModeButton
                    active={mode === CUSTOM_MODE}
                    onClick={() =>
                      handleItemChange('tokenization', CUSTOM_MODE)
                    }
                  >
                    {t('component.tokenHighlight.custom')}
                  </ModeButton>
                </Popover>
              </FlexContainer>
            </Container>
            {mode === CUSTOM_MODE && (
              <RefContext.Provider value={{ forwardedRef: containerRef }}>
                <MathSpan
                  onMouseUp={handleCustomTokenize}
                  onClick={handleCustomTokenClick}
                  selectableText
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(customTokenTemplate),
                  }}
                  className="token edu-highlight-token"
                />
              </RefContext.Provider>
            )}
            {mode !== CUSTOM_MODE &&
              template.map((el, i) => (
                <MathSpan
                  onClick={handleTemplateClick(i)}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(el.value) }}
                  key={i}
                  className={el.active ? `active-word token ${mode}` : 'token'}
                />
              ))}
          </>
        ) : null}
      </Question>
    </TemplateWrapper>
  )
}

Template.propTypes = {
  item: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  templateTab: PropTypes.number.isRequired,
  setTemplateTab: PropTypes.func.isRequired,
  setQuestionData: PropTypes.func.isRequired,
  setTemplate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  fillSections: PropTypes.func,
  cleanSections: PropTypes.func,
}

Template.defaultProps = {
  fillSections: () => {},
  cleanSections: () => {},
}

export default withNamespaces('assessment')(Template)
